import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "./styles.css";
import { API_BASE_URL } from "../../config/baseUrls";

const EventsEnhanced = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem("JWTtoken");
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEvent = async (eventId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/products/${eventId}`,
        { method: "GET" }
      );

      // The event API needs a login; eventId is already saved, so login returns to checkout
      if (response.status === 401) {
        navigate("/events/login");
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch event details");
      }

      const result = await response.json();
      setEventData(result.data.product);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReserveClick = () => {
    if (!isLoggedIn) {
      navigate("/events/login");
    } else {
      navigate("/events/checkout");
    }
  };

  useEffect(() => {
    localStorage.setItem("eventId", eventId);
    fetchEvent(eventId);
  }, [eventId]);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-IN", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: eventData.title,
          text: eventData.description,
          url: window.location.href,
        });
      } catch (err) {
        console.log("Error sharing:", err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  const calculateAvailabilityPercentage = () => {
    if (!eventData) return 0;
    const { targetOrderQuantity, availableQuantity } = eventData;
    const sold = targetOrderQuantity - availableQuantity;
    return (sold / targetOrderQuantity) * 100;
  };

  if (loading) {
    return (
      <div className="events-container-enhanced">
        <div className=" loading-state">
          <div className="spinner loading-state"></div>
          <p>Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-container-enhanced">
        <div className="error-state">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              fill="currentColor"
            />
          </svg>
          <h3>Oops! Something went wrong</h3>
          <p>{error}</p>
          <button onClick={() => fetchEvent(eventId)} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="events-container-enhanced">
        <div className="error-state">
          <h3>Event not found</h3>
          <button onClick={() => navigate(-1)} className="retry-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { projectStats } = eventData;
  const votesAvailable = eventData.isDonorPass ? 3 : 0;
  const votesUsed = projectStats?.currentVotes || 0;
  const dateTime = formatDateTime(eventData.eventDateTime);
  const availabilityPercent = calculateAvailabilityPercentage();

  return (
    <div className="events-container-enhanced">
      <div className="events-content-enhanced">
        <div className="event-card-enhanced">
          {/* Event Banner */}
          <div className="event-banner-enhanced">
            <img
              src={eventData.primaryImage}
              alt={eventData.title}
              className="banner-image-enhanced"
            />
            {eventData.category && (
              <div className="category-badge">
                {eventData.category.toUpperCase()}
              </div>
            )}
          </div>

          {/* Creator Info */}
          {/* <div className="creator-info-enhanced">
            <div className="creator-avatar-enhanced">
              {eventData.creator.profileImage ? (
                <img
                  src={eventData.creator.profileImage}
                  alt={eventData.creator.nickname}
                />
              ) : (
                <div className="avatar-placeholder-enhanced">
                  {eventData.creator.nickname.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <span className="creator-name-enhanced">
              @{eventData.creator.nickname}
            </span>
          </div> */}

          {/* Event Title */}
          <h2 className="event-title-enhanced">{eventData.title}</h2>

          {/* Event Details Grid */}
          <div className="event-details-grid">
            <div className="detail-card">
              <svg
                className="detail-icon-enhanced"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5zm2 4h5v5H7v-5z"
                  fill="currentColor"
                />
              </svg>
              <div>
                <div className="detail-label">Date</div>
                <div className="detail-value">{dateTime.date}</div>
              </div>
            </div>

            <div className="detail-card">
              <svg
                className="detail-icon-enhanced"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"
                  fill="currentColor"
                />
              </svg>
              <div>
                <div className="detail-label">Time</div>
                <div className="detail-value">{dateTime.time}</div>
              </div>
            </div>

            <div className="detail-card location-card">
              <svg
                className="detail-icon-enhanced"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                  fill="currentColor"
                />
              </svg>
              <div>
                <div className="detail-label">Location</div>
                <div className="detail-value">
                  {eventData.venue}, {eventData.city}
                </div>
              </div>
            </div>
          </div>

          {/* Pricing - Only show if NOT a donor pass */}
          {!eventData.isDonorPass && (
            <div className="pricing-section">
              <div className="price-breakdown">
                <div className="price-item">
                  <span>Ticket Price</span>
                  <span>₹{eventData.pricing.unitPrice}</span>
                </div>
                {eventData.pricing.taxAmount > 0 && (
                  <div className="price-item">
                    <span>Tax</span>
                    <span>₹{eventData.pricing.taxAmount}</span>
                  </div>
                )}
                <div className="price-item total">
                  <span>Total</span>
                  <span className="total-price">
                    ₹{eventData.pricing.totalPrice}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Reserve/Buy Button */}
        <button
          className="reserve-button-enhanced"
          onClick={handleReserveClick}
        >
          <span>{eventData.isDonorPass ? "Reserve Your Spot" : "Buy Now"}</span>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 5v2h6.59L4 18.59 5.41 20 17 8.41V15h2V5z"
              fill="currentColor"
            />
          </svg>
        </button>

        {/* About Section */}
        <div className="about-section-enhanced">
          <h3 className="about-title-enhanced">About the Event</h3>
          <p className="about-description-enhanced">{eventData.description}</p>
        </div>

        <div className="legal-links">
          <Link to="/terms-and-conditions" className="legal-link">
            Terms &amp; Conditions
          </Link>
          <span className="legal-separator">•</span>
          <Link to="/privacy-policy" className="legal-link">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EventsEnhanced;
